import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const PropertyViewingsViewDetails = () => {
  const { id } = useParams();
  const [viewing, setViewing] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadViewingDetails();
  }, [id]);

  const loadViewingDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/property_viewings/${id}/view-details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setViewing(response.data.data.viewing);
        setProperty(response.data.data.property);
        console.log('✅ [FRONTEND] Viewing details loaded:', response.data.data);
      } else {
        toast.error('Failed to load viewing details.');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading viewing details:', error);
      toast.error('Failed to load viewing details.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (confirmationId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/property_viewings_confirmations/change-status/${confirmationId}/${newStatus}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success('Confirmation status updated successfully!');
      // Reload the viewing details to show updated status
      loadViewingDetails();
    } catch (error) {
      console.error('❌ [FRONTEND] Error changing status:', error);
      toast.error('Failed to update confirmation status.');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format: "January 15, 2024, 10:00 am" (matching Laravel format)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="letting-agent-dashboard">
        <section className="letting-agent__header" style={{ backgroundColor: '#12274e' }}>
          <div className="container">
            <div className="row">
              <div className="col-md-12" style={{ paddingTop: 40, paddingBottom: 40 }}>
                <div className="wrapper">
                  <h2 className="col-white">Loading...</h2>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!viewing) {
    return (
      <div className="letting-agent-dashboard">
        <section className="letting-agent__header" style={{ backgroundColor: '#12274e' }}>
          <div className="container">
            <div className="row">
              <div className="col-md-12" style={{ paddingTop: 40, paddingBottom: 40 }}>
                <div className="wrapper">
                  <h2 className="col-white">Viewing not found</h2>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="letting-agent-dashboard">
      {/* Header Section - Matching Laravel exactly */}
      <section className="letting-agent__header" style={{ backgroundColor: '#12274e' }}>
        <div className="container">
          <div className="row js-property-viewing-of-user">
            <div className="col-md-12" style={{ paddingTop: 40, paddingBottom: 40 }}>
              <div className="wrapper">
                <h2 className="col-white">Viewings for {property?.name}</h2>
                <h4 className="col-white">{formatDateTime(viewing.date)}</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section style={{ marginTop: 20 }}>
        <div className="container">
          <div className="panel panel-default panel-standard">
            <div id="property-status-span" className="panel-heading panel-title">
              <h3>Viewing Attendees</h3>
            </div>
            <div className="panel-body">
              {viewing.confirmations && viewing.confirmations.length > 0 ? (
                <div className="dataTable_wrapper">
                  <table className="table table-hover dt-responsive">
                    <thead>
                      <tr>
                        <th>Guardian</th>
                        <th>Guardian ID</th>
                        <th>Confirmed</th>
                        <th className="action-head">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewing.confirmations.map((confirmation) => (
                        <tr key={confirmation.id}>
                          <td>
                            {confirmation.guardian && confirmation.guardian.user ? (
                              <Link to={`/guardians/${confirmation.guardian.id}`}>
                                {confirmation.guardian.user.fullName}
                              </Link>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            {confirmation.guardian?.unique_guardian_number || '-'}
                          </td>
                          <td>{confirmation.statusName || '-'}</td>
                          <td className="text-center">
                            {/* Going Button - Show only when status is NOT "Going" (0) */}
                            {confirmation.status !== 0 && (
                              <button
                                onClick={() => handleChangeStatus(confirmation.id, 0)}
                                className="btn btn-sm btn-primary hidden-print-link"
                                style={{ marginRight: 5 }}
                              >
                                Going
                              </button>
                            )}

                            {/* Attended Button - Show only when status is NOT "Attended" (1) */}
                            {confirmation.status !== 1 && (
                              <button
                                onClick={() => handleChangeStatus(confirmation.id, 1)}
                                className="btn btn-sm btn-success hidden-print-link"
                                style={{ marginRight: 5 }}
                              >
                                Attended
                              </button>
                            )}

                            {/* Not Attended Button - Show only when status is NOT "Not Attended" (2) */}
                            {confirmation.status !== 2 && (
                              <button
                                onClick={() => handleChangeStatus(confirmation.id, 2)}
                                className="btn btn-sm btn-danger hidden-print-link"
                                style={{ marginRight: 5 }}
                              >
                                Not Attended
                              </button>
                            )}

                            {/* Place Guardian Button - Always show if property has vacant rooms (matching Laravel) */}
                            {property?.hasVacantRooms && (
                              <Link
                                to={`/guardian-placement/step1/${viewing.property_id}/${confirmation.guardian_id}`}
                                className="btn btn-sm btn-primary hidden-print-link"
                              >
                                Place Guardian
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>No Users Confirmed</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertyViewingsViewDetails; 